import React, { FormEvent, useEffect, useRef, useState } from "react";
import markSrc from "./base-app/components/handset/stealthos-mark.png";
import wordmarkSrc from "./base-app/components/handset/stealthos-wordmark.png";
import "./waitlist.css";

const PAGE_TITLE = "Join the waitlist · StealthOS";
// A light check for typos; the server applies the full rules.
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const WAITLIST_ERRORS = {
  invalid: "Please enter a valid email",
  unavailable: "We couldn't add you right now. Please try again shortly.",
  offline: "Couldn't reach StealthOS. Check your connection and try again.",
} as const;

function errorForStatus(status: number) {
  if (status === 400) return WAITLIST_ERRORS.invalid;
  return WAITLIST_ERRORS.unavailable;
}

export default function WaitlistPage() {
  const [email, setEmail] = useState("");
  const [trap, setTrap] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);
  const [error, setError] = useState("");
  const doneHeading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const previous = document.title;
    document.title = PAGE_TITLE;
    return () => {
      document.title = previous;
    };
  }, []);

  useEffect(() => {
    if (joined) doneHeading.current?.focus();
  }, [joined]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const value = email.trim();
    if (value.length > 254 || !EMAIL_SHAPE.test(value)) {
      setError(WAITLIST_ERRORS.invalid);
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const response = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: value, trap }),
      });
      if (response.ok) {
        setJoined(true);
        return;
      }
      setError(errorForStatus(response.status));
    } catch {
      setError(WAITLIST_ERRORS.offline);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="waitlist">
      <div className="waitlist-inner">
        <a href="/" className="waitlist-brand" aria-label="StealthOS home">
          <img className="waitlist-mark" src={markSrc} alt="" />
          <img className="waitlist-word" src={wordmarkSrc} alt="StealthOS" />
        </a>
        <p className="waitlist-eyebrow">Private finance</p>

        {joined ? (
          <section className="waitlist-panel waitlist-done" aria-live="polite">
            <span className="waitlist-check" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path
                  d="M5 12.5l4.5 4.5L19 7.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <h1 ref={doneHeading} tabIndex={-1}>
              You're on the list
            </h1>
            <p className="waitlist-copy">
              We'll email you when your StealthOS access is ready.
            </p>
          </section>
        ) : (
          <form className="waitlist-panel" onSubmit={handleSubmit} noValidate>
            {/* Just the field and the button on screen; the heading and the
                label stay for screen readers. */}
            <h1 className="waitlist-sr-only">Join the waitlist</h1>
            <label className="waitlist-sr-only" htmlFor="waitlist-email">
              Email
            </label>
            <input
              id="waitlist-email"
              className="waitlist-input"
              type="email"
              name="email"
              autoComplete="email"
              inputMode="email"
              placeholder="you@example.com"
              maxLength={254}
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                if (error) setError("");
              }}
              aria-invalid={error === WAITLIST_ERRORS.invalid || undefined}
              aria-describedby="waitlist-error"
            />
            <div className="waitlist-trap" aria-hidden="true">
              <label htmlFor="waitlist-trap">Leave this field empty</label>
              <input
                id="waitlist-trap"
                name="trap"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={trap}
                onChange={(event) => setTrap(event.target.value)}
              />
            </div>
            <button
              className="waitlist-submit"
              type="submit"
              disabled={submitting}
            >
              {submitting ? "Joining..." : "Join waitlist"}
            </button>
            <p
              id="waitlist-error"
              className="waitlist-error"
              role="status"
              aria-live="polite"
            >
              {error}
            </p>
            <p className="waitlist-fine">
              We only use your email to update you on StealthOS access.
              Unsubscribe anytime.
            </p>
          </form>
        )}
      </div>
    </main>
  );
}
